import { useMemo, useState } from "react";
import type { FilterItem } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { buildFilterPayload } from "@features/vessel-call/grid/utils";
import {
  drybulkCargoOperationsService,
  type CargoOperationSearchRequest,
  type OperationType,
} from "@services/drybulkCargoOperationsApi";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import {
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
  GRID_REFETCH_INTERVAL_MS,
} from "../constants";
import { wholeNumberFilter } from "../utils/wholeNumberFilter";
import { GRID_PAGE_SIZE_OPTIONS } from "./useCargoOperationsSearch";

export { GRID_PAGE_SIZE_OPTIONS };
const DEFAULT_PAGE_SIZE = 20;
const SEARCH_INQUIRY_ID = "drybulk_cargo_operations.queue.search";

const inquiry = (action: string) => ({
  module: DRYBULK_CARGO_OPS_TELEMETRY.module,
  screen: DRYBULK_CARGO_OPS_TELEMETRY.queueScreen,
  action,
  entityType: "cargo_operation",
});

const text = (filters: FilterItem[], field: string): string | undefined => {
  const value = filters.find((item) => item.field === field)?.value;
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
};

export const buildQueueCriteria = (
  filters: FilterItem[],
  page: number,
  pageSize: number,
): CargoOperationSearchRequest => {
  const vesselImo = text(filters, "vesselImo");
  const vcn = text(filters, "vcn");
  const operationNumberText = text(filters, "operationNumber");
  const operationNumber =
    operationNumberText !== undefined && /^\d+$/.test(operationNumberText)
      ? Number(operationNumberText)
      : undefined;
  const nominationRefOrderNumber = wholeNumberFilter(text(filters, "nominationRefOrderNumber"));
  const operationType = text(filters, "operationType");
  const gradeSize = text(filters, "gradeSize");
  const vesselType = text(filters, "vesselType");
  const flag = text(filters, "flag");
  const agency = text(filters, "agency");
  const lastPort = text(filters, "lastPort");
  const nextPort = text(filters, "nextPort");
  const arrivalLocation = text(filters, "arrivalLocation");
  const status = text(filters, "status");
  const vesselCallStatus = text(filters, "vesselCallStatus");
  const vesselCallStatuses = vesselCallStatus
    ? buildFilterPayload(1, 1, [{ field: "status", value: vesselCallStatus }]).filters[0]?.values
    : undefined;
  return {
    ...(vesselImo ? { vesselImo } : {}),
    ...(operationNumber !== undefined ? { operationNumber } : {}),
    ...(nominationRefOrderNumber !== undefined ? { nominationRefOrderNumber } : {}),
    ...(vcn ? { vcn } : {}),
    ...(operationType ? { operationTypes: [operationType as OperationType] } : {}),
    ...(gradeSize ? { gradeSizes: [gradeSize] } : {}),
    ...(vesselType ? { vesselTypes: [vesselType] } : {}),
    ...(flag ? { flags: [flag] } : {}),
    ...(agency ? { agencyCodes: [agency] } : {}),
    ...(lastPort ? { lastPorts: [lastPort] } : {}),
    ...(nextPort ? { nextPorts: [nextPort] } : {}),
    ...(arrivalLocation ? { arrivalLocations: [arrivalLocation] } : {}),
    ...(vesselCallStatuses ? { vesselCallStatuses } : {}),
    ...(status ? { statuses: [status] } : {}),
    sortBy: "CREATED_AT",
    sortDirection: "DESC",
    pageNumber: page - 1,
    pageSize,
  };
};

export const useOperationsQueueSearch = ({
  pausePolling = false,
}: { pausePolling?: boolean } = {}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<FilterItem[]>([]);

  const criteria = useMemo(
    () => buildQueueCriteria(filters, page, pageSize),
    [filters, page, pageSize],
  );

  const query = useQuery({
    queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.search(criteria),
    queryFn: () => drybulkCargoOperationsService.search(criteria),
    refetchInterval: pausePolling ? false : GRID_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: !pausePolling,
    placeholderData: keepPreviousData,
    retry: false,
  });
  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    error: query.error,
    dataUpdatedAt: query.dataUpdatedAt,
  });

  const applyFilters = (next: FilterItem[]) => {
    startInquiryTiming(SEARCH_INQUIRY_ID, inquiry("filter_operations_queue"));
    setFilters(next);
    setPage(1);
  };

  const clearFilters = () => {
    if (filters.length > 0) {
      startInquiryTiming(SEARCH_INQUIRY_ID, inquiry("filter_operations_queue"));
    }
    setFilters([]);
    setPage(1);
  };

  const changePage = (nextPage: number, nextPageSize: number) => {
    if (nextPage !== page || nextPageSize !== pageSize) {
      startInquiryTiming(SEARCH_INQUIRY_ID, inquiry("page_operations_queue"));
    }
    setPage(nextPage);
    setPageSize(nextPageSize);
  };

  return {
    rows: query.data?.records ?? [],
    totalRecordCount: query.data?.totalRecordCount ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    page,
    pageSize,
    filters,
    applyFilters,
    clearFilters,
    changePage,
    criteria,
  };
};
